import { sendGAEvent } from '@next/third-parties/google';
import { IAirportItem } from 'app/components/Common/AirportSelect/Item';

export enum FB_EVENT {
	SEARCH = 'Search',
}

export const emitGAEventSetUserId = (id: string) => {
	if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
		window.gtag('set', {
			user_id: id,
		});
	}
};

export const emitFBEventSetUserId = (id: string) => {
	if (typeof window !== 'undefined' && (window as any).fbq) {
		(window as any).fbq('set', 'userData', { external_id: id });
	}
};

export const emitFBEvent = (eventName: string, data?: any, options?: any) => {
	if (typeof window !== 'undefined' && (window as any).fbq) {
		if (options) {
			(window as any).fbq('track', eventName, data, options);
		} else {
			(window as any).fbq('track', eventName, data);
		}
	}
};

export const emitPageViewFBEvent = () => {
	emitFBEvent('PageView');
};

// Mirrors the GTM "Search" fbq tag: standard Search params + a dedup eventID. The GTM tag
// passed eventID = {{dlv - FB Pixel External Id}} = `${uid}${imid}`, so a server-side CAPI
// Search event can be de-duplicated against this browser pixel. Kept identical for parity.
export const emitSearchEventFBEvent = (data: any, eventID?: string) => {
	emitFBEvent(FB_EVENT.SEARCH, data, eventID ? { eventID } : undefined);
};

// ─── Shared dispatcher ────────────────────────────────────────────────────────

export const emitGAEventBase = (eventName: string, data?: Record<string, any>) => {
	const payload = { ...data }
	if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
		window.gtag('event', eventName, payload)
	} else {
		sendGAEvent('event', eventName, payload)
	}
}

// ─── Legacy helpers (preserved) ───────────────────────────────────────────────

export const sendCommonGAEvent = (eventName: CommonGAEvents, data?: Record<string, any>) => {
	emitGAEventBase(eventName, data)
}

export enum CommonGAEvents {
	GOOGLE_SIGN_IN = 'google_sign_in',
}

export const emitGoogleSignInGAEvent = () => {
	emitGAEventBase(CommonGAEvents.GOOGLE_SIGN_IN)
}

// ─── Hotels GA4 Events (SHP-189) ─────────────────────────────────────────────
export enum GAHotelsEvents {
	HOME_PAGE_VIEW = 'home_page_view',
	LOCATION_CLICK = 'location_click',
	LOCATION_ITEM_SELECT = 'location_item_select',
	DATE_CLICK = 'date_click',
	FROM_DATE = 'from_date',
	TO_DATE = 'to_date',
	GUEST_CLICK = 'guest_click',
	GUESTS_SELECT = 'guests_select',
	SEARCH_WITHOUT_LOCATION = 'search_without_location',
	SEARCH_SUBMISSION = 'search_submission',
	SIGN_UP_DEALS = 'sign_up_deals',
}

export const emitPageViewGAEvent = (vert: string) => {
	emitGAEventBase(`${vert}_page_view`);
};

export const emitHomePageViewGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.HOME_PAGE_VIEW)

export const emitLocationClickGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.LOCATION_CLICK)


export const emitLocationItemSelectGAEvent = (
	suggestion: Omit<IAirportItem, 'resolvedDestination'>
) => emitGAEventBase(GAHotelsEvents.LOCATION_ITEM_SELECT, suggestion as Record<string, any>)

export const emitDateClickGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.DATE_CLICK)

export const emitFromDateGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.FROM_DATE)

export const emitToDateGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.TO_DATE)

export const emitGuestsClickGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.GUEST_CLICK)

export const emitGuestsSelectGAEvent = (data: {
	rooms: number;
	adults: number;
	children: number;
	total: number;
}) => emitGAEventBase(GAHotelsEvents.GUESTS_SELECT, data)

export const emitSearchWithoutLocationGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.SEARCH_WITHOUT_LOCATION)

export const emitSearchSubmissionGAEvent = (data: {
	value: string;
	checkIn: string;
	checkOut: string;
	rooms: { adults: number; children: number }[];
}) => {
	const totalGuests = data.rooms.reduce(
		(acc, r) => acc + r.adults + r.children,
		0
	)
	emitGAEventBase(GAHotelsEvents.SEARCH_SUBMISSION, {
		search_value: data.value,
		check_in_date: data.checkIn,
		check_out_date: data.checkOut,
		rooms_guests: `${data.rooms.length} rooms, ${totalGuests} guests`,
	})
}

export const emitSignUpDealsGAEvent = () =>
	emitGAEventBase(GAHotelsEvents.SIGN_UP_DEALS)

export const emitSetSiteLanguage = (lng: string) => {
	emitGAEventBase('site_language', { site_language: lng });
};
